import { Controller } from '@slal/ui/react-hook-form/core';
import { TextAreaField } from '@slal/ui/react-hook-form/fields';
import { ShowWhen } from '@slal/ui/react-hook-form/utils';
import { P } from '@slal/ui/typography';
import { useEffect, useState } from 'react';
import { Card, Checkbox, Field, ImageUpload, TextField, useAnalytics } from '~/components';
import { usePersonaliseCardData } from './hooks';
import { strings } from './strings';
import { CharacterCount, CheckboxTextAreaBlock, CheckboxWrapper } from './styles';

export const PersonaliseCard = () => {
    const { pushEvent } = useAnalytics();
    const { control, welcomeNote, introductionNote, finalComment, setValue, handleImageUpload, getValues } =
        usePersonaliseCardData();

    const [localLogo, setLocalLogo] = useState<string | undefined>(undefined);
    const [localScale, setLocalScale] = useState<number>(100);

    const getCharsRemainingMessage = (value: string | undefined, maxChars: number) => {
        const remaining = maxChars - (value ?? '').length;
        return remaining === 1 ? `${remaining} character remaining` : `${remaining} characters remaining`;
    };

    const saveToLocalStorage = () => {
        const values = getValues();
        const dataToSave = {
            firmName: values.firmName,
            adviserName: values.adviserName,
            logo: values.logo,
            signedUrl: values.signedUrl,
            scale: values.scale,
            rememberDetails: values.rememberDetails,
            logoImageId: values.logoImageId,
        };
        localStorage.setItem('personaliseCard', JSON.stringify(dataToSave));
    };

    const restoreFromLocalStorage = () => {
        const data = localStorage.getItem('personaliseCard');
        if (data) {
            const parsedData = JSON.parse(data);
            setValue('firmName', parsedData.firmName || '');
            setValue('adviserName', parsedData.adviserName || '');
            setValue('logoImageId', parsedData.logoImageId || '');
            setValue('scale', parsedData.scale || '');
            setValue('rememberDetails', parsedData.rememberDetails || false);

            setLocalLogo(parsedData.logo || undefined);
            setLocalScale(parsedData.scale || 100);
        }
    };

    useEffect(() => {
        restoreFromLocalStorage();
    }, []);

    return (
        <>
            <Card heading={strings.personalise}>
                <Field label={strings.firmName} labelFor="firmName">
                    <TextField maxLength={80} name="firmName" size={5} />
                </Field>
                <Field label={strings.adviserName} labelFor="adviserName">
                    <TextField maxLength={80} name="adviserName" size={5} />
                </Field>
                <Field label={strings.logo} labelFor="logo">
                    <ImageUpload
                        id="logo"
                        imageUploadHelpText={strings.imageUploadHelpText}
                        scalingHelpText={strings.scalingHelpText}
                        maximumFileSize={5000000}
                        resizedImageSize={500}
                        onChange={async value => {
                            setValue('logo', value);
                            await handleImageUpload(value);
                            pushEvent({
                                eventCategory: 'Customise your report',
                                eventAction: 'Logo',
                                eventLabel: 'Logo attached',
                            });
                            setLocalLogo(value);
                        }}
                        onScaleChange={value => setValue('scale', value)}
                        imageDataURL={localLogo}
                        imageScale={localScale}
                    />
                </Field>
                <CheckboxTextAreaBlock>
                    <CheckboxWrapper>
                        <Controller
                            name="includeWelcomeNote"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    {...field}
                                    checked={!!field.value}
                                    onChange={e => {
                                        const isChecked = e.target.checked;
                                        field.onChange(isChecked);
                                        pushEvent({
                                            eventCategory: 'Customise your report',
                                            eventAction: isChecked ? 'Checkbox- selected' : 'Checkbox- deselected',
                                            eventLabel: 'Include a welcome note for your client',
                                        });
                                    }}
                                    aria-label="Include welcome note in the PDF"
                                />
                            )}
                        />
                        <P>{strings.includeWelcomeNote}</P>
                    </CheckboxWrapper>
                    <ShowWhen fieldName="includeWelcomeNote" valueEquals={true}>
                        <TextAreaField name="welcomeNote" maxLength={1000} rows={4} style={{ resize: 'vertical' }} />
                        <CharacterCount>{getCharsRemainingMessage(welcomeNote, 1000)}</CharacterCount>
                    </ShowWhen>
                </CheckboxTextAreaBlock>
                <CheckboxTextAreaBlock>
                    <CheckboxWrapper>
                        <Controller
                            name="includeIntroductionNote"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    {...field}
                                    checked={!!field.value}
                                    onChange={e => {
                                        const isChecked = e.target.checked;
                                        field.onChange(isChecked);
                                        pushEvent({
                                            eventCategory: 'Customise your report',
                                            eventAction: isChecked ? 'Checkbox- selected' : 'Checkbox- deselected',
                                            eventLabel: 'Include an introduction',
                                        });
                                    }}
                                    aria-label="Include welcome note in the PDF"
                                />
                            )}
                        />
                        <P>{strings.includeIntroductionNote}</P>
                    </CheckboxWrapper>
                    <ShowWhen fieldName="includeIntroductionNote" valueEquals={true}>
                        <TextAreaField
                            name="introductionNote"
                            maxLength={1000}
                            rows={4}
                            style={{ resize: 'vertical' }}
                        />
                        <CharacterCount>{getCharsRemainingMessage(introductionNote, 1000)}</CharacterCount>
                    </ShowWhen>
                </CheckboxTextAreaBlock>
                <CheckboxTextAreaBlock>
                    <CheckboxWrapper>
                        <Controller
                            name="includeFinalComment"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    {...field}
                                    checked={!!field.value}
                                    onChange={e => {
                                        const isChecked = e.target.checked;
                                        field.onChange(isChecked);
                                        pushEvent({
                                            eventCategory: 'Customise your report',
                                            eventAction: isChecked ? 'Checkbox- selected' : 'Checkbox- deselected',
                                            eventLabel: 'Include a summary',
                                        });
                                    }}
                                    aria-label="Include final comment in the PDF"
                                />
                            )}
                        />
                        <P>{strings.includeFinalComment}</P>
                    </CheckboxWrapper>
                    <ShowWhen fieldName="includeFinalComment" valueEquals={true}>
                        <TextAreaField name="finalComment" maxLength={1000} rows={4} style={{ resize: 'vertical' }} />
                        <CharacterCount>{getCharsRemainingMessage(finalComment, 1000)}</CharacterCount>
                    </ShowWhen>
                </CheckboxTextAreaBlock>
            </Card>
            <CheckboxWrapper>
                <Controller
                    name="rememberDetails"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            {...field}
                            name="rememberDetails"
                            checked={!!field.value}
                            onChange={e => {
                                const isChecked = e.target.checked;
                                field.onChange(isChecked);
                                pushEvent({
                                    eventCategory: 'Customise your report',
                                    eventAction: isChecked ? 'Checkbox- selected' : 'Checkbox- deselected',
                                    eventLabel: 'Remember firm name, adviser name and logo for future use',
                                });
                                if (isChecked) {
                                    saveToLocalStorage();
                                } else {
                                    localStorage.removeItem('personaliseCard');
                                }
                            }}
                            aria-label="Remember my details for future visits"
                        />
                    )}
                />
                <P>{strings.rememberDetails}</P>
            </CheckboxWrapper>
        </>
    );
};
